import { Card, CardContent } from "@/components/ui/card";
import { FACHKRAEFTE_TEXT, gross } from "@/config/webseiteFakten";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Quote, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import circleImg from "@/assets/circle-watercolor.png";
import rasselnImg from "@/assets/rasseln-watercolor.png";
import ukuleleImg from "@/assets/ukulele-watercolor.png";
import silkeHamann from "@/assets/testimonials/silke-hamann.jpg";
import kimberlyWesselmann from "@/assets/testimonials/kimberly-wesselmann.jpg";
import melanieGoeken from "@/assets/testimonials/melanie-goeken.jpg";
import lauraJansen from "@/assets/testimonials/laura-jansen.jpg";
import nicoleDuering from "@/assets/testimonials/nicole-duering.jpg";

const testimonials = [
  {
    name: "Silke Hamann",
    role: "Erzieherin & Kursleiterin",
    text: "Ich bin seit Jahren Mitglied bei Musikonzept. Mit den Inhalten aus den Fortbildungen und der riesigen \"Bibliothek\" an musikalischen Angeboten begeistere ich täglich meine Kinder (und Kollegen und Eltern) in der Kita und in meinen Eltern-Kind-Kursen. Inzwischen hab ich die Ausbildung zur Fachkraft gemacht. Ich bin sehr, sehr dankbar, Musikonzept entdeckt zu haben!",
    rating: 5,
    image: silkeHamann,
  },
  {
    name: "Kimberly Wesselmann",
    role: "Erzieherin U3",
    text: "Ich bin total begeistert von dieser Plattform. Ihr inspiriert mich musikalisch mit euren wunderbaren Ideen. Auch die Ausbildung Fachkraft für Musik hat mich total bereichert.",
    rating: 5,
    image: kimberlyWesselmann,
  },
  {
    name: "Melanie Göken",
    role: "Erzieherin, Fachkraft für Musik im Elementarbereich",
    text: "Musikonzept ist für meine pädagogische Arbeit eine echte Bereicherung. Durch die vielfältigen Inhalte habe ich jederzeit Zugang zu aktuellen, praxisrelevanten Themen. Alles ist klar verständlich aufbereitet und sofort umsetzbar. Eine großartige Plattform, die meine Arbeit nachhaltig unterstützt!",
    rating: 5,
    image: melanieGoeken,
  },
  {
    name: "Laura Jansen",
    role: "Erzieherin",
    text: "Ich bin total begeistert von MusiKonzept! Obwohl ich noch neu bin, gelingt es mir endlich, Musik und Pädagogik mit Freude in den Alltag einzubauen. Die Kinder lieben es, und unser Morgenkreis macht wieder richtig Spaß.",
    rating: 5,
    image: lauraJansen,
  },
  {
    name: "Nicole Düring",
    role: "Kinderpflegerin und Fachkraft für Musik im Elementarbereich",
    text: "Ich bin sehr froh, auf MusiKonzept gestoßen zu sein. Eine absolute Bereicherung für jeden Erzieher, Kinderpfleger, Auszubildende und Musikpädagogen. Es ist schwer, Fortbildungen für Fachkräfte zu finden, die so viele verschiedene Sparten professionell abdecken – hier füllt MusiKonzept endlich eine wichtige Lücke. Was mich dabei besonders überzeugt, ist die hervorragende Balance in den Workshops: Es gibt zwar einen fundierten theoretischen Unterbau, aber der Fokus liegt ganz klar auf der praktischen Umsetzbarkeit. Man geht mit konkreten Ideen aus den Workshops heraus, die man sofort in der Kita oder im Unterricht anwenden kann. Genau so muss moderne Musikpädagogik-Weiterbildung aussehen",
    rating: 5,
    image: nicoleDuering,
  },
];

const TestimonialsSection = () => {
  const [api, setApi] = useState<any>();
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  
  const toggleExpanded = (index: number) => {
    setExpandedCards(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gradient-start via-background to-gradient-end opacity-40" />
      
      {/* Decorative watercolor backgrounds - subtle and decorative */}
      <div className="absolute bottom-0 left-0 w-96 h-96 opacity-[0.03] -translate-x-1/3 translate-y-1/3 pointer-events-none">
        <img src={circleImg} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="absolute top-20 right-10 w-64 h-64 opacity-[0.04] rotate-12 pointer-events-none">
        <img src={ukuleleImg} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="absolute top-1/2 left-10 w-48 h-48 opacity-[0.03] -rotate-12 pointer-events-none">
        <img src={rasselnImg} alt="" className="w-full h-full object-contain" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold">
            Was unsere <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">Community</span> sagt
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {gross(FACHKRAEFTE_TEXT)} vertrauen auf MusiKonzept
          </p>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 4000,
            }),
          ]}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {testimonials.map((testimonial, index) => {
              const maxLength = 200;
              const isLongText = testimonial.text.length > maxLength;
              const isExpanded = expandedCards.includes(index);
              const displayText = isLongText && !isExpanded 
                ? truncateText(testimonial.text, maxLength)
                : testimonial.text;

              return (
              <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Card
                  className="group flex flex-col h-full transition-all duration-300 border-border/50 bg-card/95 backdrop-blur-sm hover:shadow-card-hover hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-8 space-y-6 flex flex-col flex-1">
                    {/* Profile Image & Quote Icon */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover ring-2 ring-primary/20"
                        />
                        <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                          <Quote className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      
                      {/* Rating Stars */}
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <svg
                            key={i}
                            className="w-4 h-4 fill-accent"
                            viewBox="0 0 20 20"
                          >
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                    </div>

                    {/* Testimonial Text */}
                    <div className="flex-1">
                      <p className="text-foreground leading-relaxed">
                        "{displayText}"
                      </p>
                      
                      {isLongText && (
                        <button
                          onClick={() => toggleExpanded(index)}
                          className="mt-2 text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
                        >
                          {isExpanded ? (
                            <>
                              Weniger lesen
                              <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Mehr lesen
                              <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Author */}
                    <div className="pt-4 border-t border-border/50">
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};

export default TestimonialsSection;
